/** @jsx isolateComponent */

import { keyframes } from '@emotion/core';
import { Colors } from '@zapier/design-system/dist/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

const defaultProps = {
  ariaLabel: 'Loading',
  color: 'black',
  size: 'small',
};

/** Note on (OPTIONAL) comments:
  Our current TypeScript/Styleguidist setup is making these optional props below
  display in the docs as "required." I added (OPTIONAL) to be more clear that this are
  not truly required props.
*/
type Props = {
  /** (OPTIONAL) Accessibility label for the node */
  ariaLabel?: string;

  /** (OPTIONAL) Diameter and border width of loader */
  size?: 'small' | 'medium' | 'large';

  /** (OPTIONAL) Should the loader be overlayed on top of a background */
  color?: 'white' | 'black';
} & typeof defaultProps;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sizes = {
  small: {
    diameter: 20,
    thickness: 4,
  },
  medium: {
    diameter: 30,
    thickness: 6,
  },
  large: {
    diameter: 60,
    thickness: 10,
  },
};

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    {
      animation: `${spin} 0.75s linear infinite`,
      borderStyle: 'solid',
      borderRightColor: 'transparent',
      borderRadius: '50%',
      opacity: 0.25,
    },
    withModifier(props.size, {
      height: sizes[props.size].diameter,
      width: sizes[props.size].diameter,
      borderWidth: `${sizes[props.size].thickness}px`,
    }),
    withModifier(props.color, {
      borderColor: Colors[props.color],
      borderRightColor: 'transparent',
    }),
  ],
});

/**
 * Renders a spinner that indicates that something async is happening.
 */

export const Spinner = (props: Props) => {
  return (
    <div
      aria-label={props.ariaLabel}
      css={Styles.root(props)}
      data-testid="spinner"
    />
  );
};

Spinner.defaultProps = defaultProps;
